import Login from '@src/application/login.vue';
import { closeLoader, isLoading, openLoader } from '@utils/loader-util.js';
import { ApiError } from '@src/api/api-error.js';
import { CancelError } from '@src/api/cancel-error.js';
import { createErrorModal, createModal } from '@utils/modal-util.js';
import { createApiErrorModal } from '@src/api/api-client.js';
import { RequestError } from '@src/api/request-error.js';
import loginModalStore from '@src/application/store/login-modal-store.js';
import { RequestConfig } from '@src/api/request-config.js';
import { type Dictionary } from '@src/application/dictionary.js';
import { RequestResponse } from '@src/api/interface/request-response.js';
import { getCircularReferenceReplacer } from '@utils/json-utill.js';
import clientIdentifierStore from '@src/application/store/client-identifier-store.js';
import { sleep } from '@utils/sleep-util.js';
import loaderStore from '@src/application/store/loader-store.js';
import { useAuthComposable } from '@src/auth/composable/auth-composable.ts';

export class Request extends Promise<RequestResponse> {

    abortController;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    resolve: Function|null = null;

    url;

    data = null;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    reject: Function|null = null;

    showErrorModal = true;

    customErrorHttpCodes = [409];

    config = new RequestConfig();

    isCancelled: boolean = false;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    constructor(url: string|Function, method: 'GET'|'POST'|'PATCH'|'DELETE'|null = null, data: any = null) {
        if (url instanceof Function) {
            // if called as normal promise with call -> for Example in .then() - ref https://stackoverflow.com/questions/48158730/extend-javascript-promise-and-resolve-or-reject-it-inside-constructor
            // @ts-ignore
            super(url);
            return;
        }

        let resolveHandle = null;
        let rejectHandle = null;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        super((resolve: Function, reject: Function) => {
            resolveHandle = resolve;
            rejectHandle = reject;
        });

        const { authToken } = useAuthComposable();
        this.config.method = method as string;
        if (authToken.value !== null) {
            this.config.headers['X-AUTH-TOKEN'] = authToken.value;
        }
        if (clientIdentifierStore.clientId.data) {
            this.config.headers['X-CLIENT-ID'] = clientIdentifierStore.clientId.data;
        }
        if (data !== null) {
            if (data instanceof FormData) {
                this.config.body = data;
            } else {
                this.config.body = JSON.stringify(data, getCircularReferenceReplacer());
                this.config.headers['Content-Type'] = 'application/json';
            }
        }

        this.resolve = resolveHandle;
        this.reject = rejectHandle;
        this.url = url;
        this.abortController = new AbortController();
        this.config.signal = this.abortController.signal;
    }

    async fetch(): Promise<{ headers: Array<string>, data: any }> {
        if (this.reject === null || this.resolve === null) {
            throw new Error('reject or resolve must not be null');
        }

        const baseUrl = import.meta.env.BB_API_BASE_URL;
        try {
            // remove trailing and leading slashes and add one in between
            const response = await fetch(baseUrl.replace(/\/+$/, '') + '/' + this.url!.replace(/^\/+/, ''), this.config);
            return await this._handleResponse(response);
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                return this.reject(new CancelError('request aborted'));
            }
            if (e instanceof TypeError) {
                const loading = isLoading();
                if (loading) {
                    closeLoader();
                }
                await createErrorModal(
                    'Ein Netzwerkfehler ist aufgetreten',
                    'Bitte überprüfe deine Internetverbindung.',
                );
                if (loading) {
                    openLoader();
                }
                return this.fetch();
            }
            if (this.showErrorModal) {
                while (isLoading()) {
                    closeLoader();
                }
                await createErrorModal(
                    'Unbekannter Fehler',
                    e instanceof Error ? e.message : 'Ein unbekannter Fehler ist aufgetreten',
                );
            }

            return this.reject(e);
        }
    }

    disableErrorModal() {
        this.showErrorModal = false;
    }

    cancel() {
        // https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort#reason
        this.isCancelled = true;
        this.abortController?.abort();
    }

    setCustomErrorHttpCodes(...codes: number[]) {
        this.customErrorHttpCodes = codes;
    }

    async _getDecodedResponseData(response: Response) {
        let responseData = null;

        // empty response
        if (response.status === 204) {
            return null;
        }

        const contentType = response.headers.get('content-type');

        if (contentType !== null && contentType.match(/json/) !== null) {
            try {
                responseData = await response.text();
                return JSON.parse(responseData);
            } catch (e) {
                // wenn die response nicht "ok" ist, laufen wir später in Fehlerbehandlung und gehen daher hier mit null raus
                if (!response.ok) {
                    return null;
                }
                // wenn die response aber i.O. ist und das json nicht geparst werden kann, dann ist es ein Fehler
                throw e;
            }
        }

        if (contentType === 'application/octet-stream') {
            return this._handleBlob(response);
        }

        if (contentType === 'application/pdf') {
            return this._handleBlob(response);
        }

        if (contentType !== null && contentType.match('text/plain') !== null) {
            return response.text();
        }

        throw new Error(response.statusText);
    }

    async _handleBlob(response: Response) {
        return response.blob();
    }

    /* eslint-disable complexity */
    async _handleResponse(response: Response) {
        if (this.resolve === null || this.reject === null) {
            throw new Error('resolve or reject must not be null');
        }

        const responseData = await this._getDecodedResponseData(response);

        if (responseData !== null && response.status === 500 && responseData.errorCode !== undefined && responseData.errorCode === 'DEPLOYING') {
            await sleep(1000);
            loaderStore.deploying.data += 1;
            loaderStore.message.data = 'Ein Update wird installiert ... ';
            return this.fetch();
        }

        if (loaderStore.deploying.data > 0) {
            loaderStore.deploying.data = 0;
        }

        if (response.ok) {
            const headers: Dictionary<string|null> = {};
            // das muss .forEach sein, da headers ein spezielles objekt ist https://developer.mozilla.org/en-US/docs/Web/API/Headers
            response.headers.forEach((header, name) => {
                headers[name] = header;
            });
            return this.resolve({
                headers,
                data: responseData,
            });
        }

        if (response.status === 401 && this.url !== '/be/login') {
            return this.handleAuthError();
        }

        if (responseData !== null && this.customErrorHttpCodes.includes(response.status) && responseData.errorCode !== undefined && responseData.errorCode !== null) {
            return this.reject(new ApiError(responseData.errorCode, responseData.errorMessage || responseData.message || null, responseData.data || null));
        }

        const error = new RequestError(response.status, responseData !== null ? responseData.errorMessage || responseData.message || responseData.error || null : null);
        if (this.showErrorModal) {
            while (isLoading()) {
                closeLoader();
            }
            await createApiErrorModal(error);
        }
        return this.reject(error);
    }

    private async handleAuthError() {
        const { authToken } = useAuthComposable();
        authToken.value = null;

        let loaderCount = 0;
        while (isLoading()) {
            loaderCount++;
            closeLoader();
        }
        if (loginModalStore.loginModal.data === null) {
            loginModalStore.loginModal.data = createModal(false, Login, 'loggedIn');
        }
        await loginModalStore.loginModal.data;
        loginModalStore.loginModal.data = null;
        for (let i = 0; i < loaderCount; loaderCount--) {
            openLoader();
        }
        this.config.headers['X-AUTH-TOKEN'] = authToken.value!;
        return this.fetch();
    }
}
